import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_DOC_PATHS } from '../../services/legalDocumentsService';

/**
 * Permanent footer links to the public Terms of Use and Privacy Notice pages
 * (/legal/terms, /legal/privacy). The links carry no version pin, so a visitor
 * always opens the version currently published for their HOA.
 *
 * Used on the landing page (which also hosts the sign-in card) and at the bottom
 * of every portal tier layout. `lead` is an optional node shown beside the links
 * (the landing page passes its existing association copyright line).
 *
 * `tone` picks the palette: "light" for warm parchment pages (portal), "dark" for
 * the forest photo backdrop on the landing page.
 *
 * `linkState` is forwarded as router location state; the portal passes
 * { fromPortal: true } so the legal page can send a signed-in reader back.
 */

const TONES = {
  light: {
    border: 'border-amber-100/60',
    lead: 'text-slate-600',
    link: 'text-slate-600 hover:text-primary-dark focus-visible:text-primary-dark focus-visible:ring-secondary',
    divider: 'bg-slate-300',
  },
  dark: {
    border: 'border-white/10',
    lead: 'text-cream/80',
    link: 'text-cream/80 hover:text-white focus-visible:text-white focus-visible:ring-amber-300',
    divider: 'bg-white/25',
  },
};

const LEGAL_LINKS = [
  { key: 'terms', label: 'Terms of Use', to: LEGAL_DOC_PATHS.terms },
  { key: 'privacy', label: 'Privacy Notice', to: LEGAL_DOC_PATHS.privacy },
];

export const LegalFooter = ({ tone = 'light', lead = null, linkState, className = '' }) => {
  const palette = TONES[tone] || TONES.light;

  return (
    <footer
      data-testid="legal-footer"
      className={`w-full border-t ${palette.border} ${className}`}
    >
      <div className="max-w-7xl mx-auto flex flex-col-reverse items-center gap-1 sm:flex-row sm:justify-between sm:gap-4 text-xs font-medium">
        {lead ? <span className={`text-center sm:text-left ${palette.lead}`}>{lead}</span> : <span />}
        <nav aria-label="Legal">
          <ul className="flex items-center justify-center">
            {LEGAL_LINKS.map((item, index) => (
              <li key={item.key} className="flex items-center">
                {index > 0 && (
                  <span className={`block w-px h-4 ${palette.divider}`} aria-hidden="true" />
                )}
                <Link
                  to={item.to}
                  state={linkState}
                  className={`inline-flex items-center justify-center min-h-[44px] px-4 rounded-full underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 transition-colors ${palette.link}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </footer>
  );
};
